import { useState, useEffect } from 'react';
import axios from 'axios';
import Experiments, { type Report, type Money } from './Experiments';

interface Bet {
  id: number;
  strategy: string;
  question: string;
  category: string;
  side: 'YES' | 'NO';
  entry_price: number;
  market_prob: number | null;
  model_prob: number | null;
  stake: number;
  placed_at: string;
  end_date: string | null;
  status: 'OPEN' | 'WON' | 'LOST' | 'VOID';
  pnl: number | null;
  resolved_at: string | null;
  note: string | null;
}

function when(iso: string | null): string {
  if (!iso) return '—';
  const ms = new Date(iso).getTime() - Date.now();
  const h = Math.abs(ms) / 3600000;
  const span = h < 1 ? `${Math.round(h * 60)}m` : h < 48 ? `${Math.round(h)}h` : `${Math.round(h / 24)}d`;
  return ms >= 0 ? `in ${span}` : `${span} ago`;
}

const usd = (x: number) => `${x < 0 ? '-' : ''}$${Math.abs(x).toFixed(2)}`;

// Money at a glance for all strategies, or the selected one.
function Summary({ m, label }: { m: Money; label: string }) {
  const settled = m.wins + m.losses;
  return (
    <div className="panel edge-panel">
      <div className="panel-header">💰 {label}</div>
      <div className="edge-stats-row lab-summary">
        <div className="edge-stat"><div className="edge-stat-label">Paper bets placed</div><div className="edge-stat-value">{m.bets}</div><div className="edge-stat-sub">{usd(m.staked)} staked in total</div></div>
        <div className="edge-stat"><div className="edge-stat-label">In play</div><div className="edge-stat-value">{usd(m.inPlay)}</div><div className="edge-stat-sub">{m.openBets} bets waiting on a result</div></div>
        <div className="edge-stat"><div className="edge-stat-label">Settled</div><div className="edge-stat-value">{settled}</div><div className="edge-stat-sub">{m.wins} won · {m.losses} lost</div></div>
        <div className="edge-stat"><div className="edge-stat-label">Total won</div><div className="edge-stat-value exp-pos">+{usd(m.wonUsd)}</div><div className="edge-stat-sub">profit on winning bets</div></div>
        <div className="edge-stat"><div className="edge-stat-label">Total lost</div><div className="edge-stat-value exp-neg">{usd(m.lostUsd)}</div><div className="edge-stat-sub">stakes lost</div></div>
        <div className="edge-stat"><div className="edge-stat-label">Net result</div><div className={`edge-stat-value ${m.net >= 0 ? 'exp-pos' : 'exp-neg'}`}>{m.net >= 0 ? '+' : ''}{usd(m.net)}</div><div className="edge-stat-sub">{settled ? `${m.net >= 0 ? '+' : ''}${(m.net / (m.staked - m.inPlay) * 100).toFixed(1)}% on settled stakes` : 'nothing settled yet'}</div></div>
      </div>
    </div>
  );
}

// Strategy Lab tab: money summary, leaderboard (tap a strategy to filter), the actual bets below.
// "Since" presets; custom dates come from the date picker. The choice is remembered per browser.
const SINCE_PRESETS: { label: string; value: string }[] = [
  { label: 'All time', value: '' },
  { label: 'Last 24 hours', value: '24h' },
  { label: 'Last 3 days', value: '3d' },
  { label: 'Last 7 days', value: '7d' },
  { label: 'Pick a date…', value: 'custom' },
];
function sinceISO(choice: string, custom: string): string {
  const hours: Record<string, number> = { '24h': 24, '3d': 72, '7d': 168 };
  if (hours[choice]) return new Date(Date.now() - hours[choice] * 3600000).toISOString();
  if (choice === 'custom' && custom) return new Date(custom + 'T00:00:00Z').toISOString();
  return '';
}
const remembered = (k: string) => { try { return localStorage.getItem(k) || ''; } catch { return ''; } };
const remember = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { /* storage unavailable */ } };

export default function Lab() {
  const [sinceChoice, setSinceChoice] = useState(() => remembered('lab_since'));
  const [customDate, setCustomDate] = useState(() => remembered('lab_since_date'));
  const since = sinceISO(sinceChoice, customDate);
  const [report, setReport] = useState<Report | null>(null);
  useEffect(() => {
    const load = () => axios.get(`/api/experiments${since ? `?since=${encodeURIComponent(since)}` : ''}`).then(r => setReport(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [since.slice(0, 13)]);   // hour precision, so rolling presets don't refetch every render
  const [strategy, setStrategy] = useState<string | null>(null);
  const [status, setStatus] = useState<'open' | 'settled'>('open');
  const [bets, setBets] = useState<Bet[] | null>(null);

  useEffect(() => {
    const load = () => {
      const params = new URLSearchParams({ status, limit: '150' });
      if (strategy) params.set('strategy', strategy);
      if (since) params.set('since', since);
      axios.get(`/api/experiments/bets?${params}`).then(r => setBets(r.data)).catch(() => setBets([]));
    };
    setBets(null);
    load();
    const t = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(t);
  }, [strategy, status, since.slice(0, 13)]);

  return (
    <div className="lab">
      <div className="edge-detail lab-explainer">
        Simulated (paper) bets with fake money on real Polymarket markets: real prices in, real results out, no real money at risk.
        Each strategy is its own experiment with its own $10 bets — some deliberately bet <i>opposite</i> sides of the same market
        (momentum vs. reversion, favorite vs. underdog), so the combined total is not one portfolio. Judge strategies one by one.
      </div>
      <div className="lab-since">
        <label>Show bets placed</label>
        <select value={sinceChoice} onChange={e => { setSinceChoice(e.target.value); remember('lab_since', e.target.value); }}>
          {SINCE_PRESETS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
        </select>
        {sinceChoice === 'custom' && (
          <input type="date" value={customDate} onChange={e => { setCustomDate(e.target.value); remember('lab_since_date', e.target.value); }} />
        )}
      </div>
      {report?.audit && (
        <div className={`lab-audit ${report.audit.ok ? 'lab-audit-ok' : 'lab-audit-bad'}`}>
          {report.audit.ok ? '✓' : '⚠'} Settlement independently audited {new Date(report.audit.at).toLocaleString()}:
          {' '}{report.audit.checked} random settled bets re-checked against Polymarket's final results, {report.audit.mismatches ?? '?'} mismatches.
        </div>
      )}
      {report && (() => {
        const row = strategy ? report.strategies.find(r => r.strategy === strategy) : null;
        return <Summary m={row ? row.money : report.totals} label={`${row ? `${row.strategy} — money` : 'All strategies combined — money'}${since ? ` (since ${new Date(since).toLocaleDateString()})` : ''}`} />;
      })()}
      <Experiments selected={strategy} onSelect={setStrategy} report={report} />

      <div className="panel">
        <div className="panel-header lab-bets-header">
          <span>🎯 {strategy ? <b>{strategy}</b> : 'All strategies'} — bets</span>
          <span className="lab-toggle">
            <button className={status === 'open' ? 'active' : ''} onClick={() => setStatus('open')}>Open</button>
            <button className={status === 'settled' ? 'active' : ''} onClick={() => setStatus('settled')}>Settled</button>
          </span>
        </div>
        {strategy && <div className="edge-detail">Showing one strategy — <a href="#" onClick={e => { e.preventDefault(); setStrategy(null); }}>show all</a></div>}
        {bets === null ? <div className="edge-detail">Loading…</div>
          : bets.length === 0 ? <div className="edge-detail">{status === 'open' ? 'No open bets.' : 'Nothing settled yet — bets resolve as their markets close.'}</div>
          : (
            <div className="lab-bets">
              {bets.map(b => {
                const edge = b.model_prob != null ? b.model_prob - b.entry_price : null;
                return (
                  <div key={b.id} className={`lab-bet lab-${b.status.toLowerCase()}`}>
                    <div className="lab-bet-q">{b.question}</div>
                    <div className="lab-bet-meta">
                      <span className={`lab-side lab-side-${b.side.toLowerCase()}`}>{b.side} @ {(b.entry_price * 100).toFixed(1)}¢</span>
                      {!strategy && <span className="lab-tag">{b.strategy}</span>}
                      <span className="lab-tag">{b.category}</span>
                      {edge != null && <span className="lab-tag">model {(b.model_prob! * 100).toFixed(0)}% · edge {edge >= 0 ? '+' : ''}{(edge * 100).toFixed(1)}¢</span>}
                      {b.status === 'OPEN'
                        ? <span className="lab-when">resolves {when(b.end_date)}</span>
                        : <span className={(b.pnl ?? 0) >= 0 ? 'exp-pos' : 'exp-neg'}>{b.status} {(b.pnl ?? 0) >= 0 ? '+' : '-'}${Math.abs(b.pnl ?? 0).toFixed(2)}</span>}
                    </div>
                    {b.note && <div className="lab-bet-note">{b.note}</div>}
                  </div>
                );
              })}
            </div>
          )}
      </div>
    </div>
  );
}
